/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Task } from "../shared/types";
import { localDate } from "../shared/dates";
import { useStaff } from "../shared/useStaff";
import { QuickAddTask } from "./QuickAddTask";
import { TaskEditor } from "./TaskEditor";
import { TaskRow } from "./TaskRow";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

type Group = { key: string; tasks: Task[] };

// One-off jobs: call the plumber, deep-clean the hood, order napkins.
// See docs/plans/workday/readme.md.
export const WorkDayTasksPage = defineComponent({
  name: "WorkDayTasksPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const staff = useStaff();

    const status = ref<"open" | "done">("open");
    const person = ref<number | "all">("all");
    const loading = ref(true);
    const list = ref<Task[]>([]);
    const editing = ref<Task | null>(null);

    const load = async () => {
      try {
        list.value = (await trpc.workday.tasks.list.query({
          status: status.value,
        })) as Task[];
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);
    watch(status, load);

    const toggle = async (task: Task) => {
      const next = task.status === "done" ? "open" : "done";
      list.value = list.value.map((x) =>
        x.id === task.id ? { ...x, status: next } : x,
      );
      try {
        await trpc.workday.tasks.update.mutate({ id: task.id, status: next });
        setTimeout(load, 600);
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
        await load();
      }
    };

    const groups = computed<Group[]>(() => {
      const today = localDate();
      const mine = list.value.filter(
        (x) => person.value === "all" || x.assigned_to === person.value,
      );
      if (status.value === "done") return [{ key: "done", tasks: mine }];
      const by: Record<string, Task[]> = {
        overdue: [],
        today: [],
        upcoming: [],
        someday: [],
      };
      for (const x of mine) {
        const k = !x.due_date
          ? "someday"
          : x.due_date < today
            ? "overdue"
            : x.due_date === today
              ? "today"
              : "upcoming";
        by[k].push(x);
      }
      return ["overdue", "today", "upcoming", "someday"]
        .map((key) => ({ key, tasks: by[key] }))
        .filter((g) => g.tasks.length);
    });

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("workday.nav.tasks")}
          subtitle={t("workday.tasks.subtitle")}
        />
        <div class="flex-1 space-y-5 overflow-auto p-5">
          <QuickAddTask staff={staff.value} onAdded={load} />
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div role="tablist" class="tabs tabs-boxed bg-base-100">
              {(["open", "done"] as const).map((s) => (
                <button
                  key={s}
                  role="tab"
                  class={[
                    "tab tab-sm",
                    status.value === s ? "tab-active" : "",
                  ].join(" ")}
                  onClick={() => (status.value = s)}
                >
                  {t(`workday.tasks.tab_${s}`)}
                </button>
              ))}
            </div>
            <select
              class="select select-bordered select-sm"
              value={String(person.value)}
              onChange={(e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                person.value = v === "all" ? "all" : Number(v);
              }}
            >
              <option value="all">{t("workday.tasks.everyone")}</option>
              {staff.value.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          {loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : groups.value.length === 0 ? (
            <div class="flex flex-col items-center gap-2 rounded-2xl bg-base-100 py-14 text-center">
              <span class="material-symbols-outlined text-5xl text-success">
                done_all
              </span>
              <p class="text-sm text-base-content/60">
                {status.value === "open"
                  ? t("workday.tasks.empty_open")
                  : t("workday.tasks.empty_done")}
              </p>
            </div>
          ) : (
            groups.value.map((g) => (
              <section
                key={g.key}
                class="rounded-xl border border-base-300 bg-base-100 p-3"
              >
                <h2
                  class={[
                    "mb-1 px-2 text-xs font-semibold uppercase tracking-wide",
                    g.key === "overdue" ? "text-error" : "text-base-content/50",
                  ].join(" ")}
                >
                  {t(`workday.tasks.group_${g.key}`)} · {g.tasks.length}
                </h2>
                {g.tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    staff={staff.value}
                    onToggle={() => toggle(task)}
                    onOpen={() => (editing.value = task)}
                  />
                ))}
              </section>
            ))
          )}
        </div>
        {editing.value && (
          <TaskEditor
            task={editing.value}
            staff={staff.value}
            onClose={async (changed: boolean) => {
              editing.value = null;
              if (changed) await load();
            }}
          />
        )}
      </div>
    );
  },
});
